// Persistent per-browser device identifier for clinician email-MFA device trust.
//
// The Lambda's DefineAuthChallenge uses this id to look up a per-(user, device)
// trust row in DynamoDB and decide whether a 6-digit email code is required.
// It is a client-supplied bearer skip-token (see plan "Security note — accepted
// tradeoff"): unknown/forged ids fail closed (server treats them as
// 'UNREGISTERED' and forces MFA), so persisting it here is safe.
//
// Consequence to accept (plan, Portal section): clearing site data / incognito
// produces a new device id and therefore re-prompts for MFA.

const DEVICE_ID_STORAGE_KEY = 'percix_device_id';

// Must match the Lambda's validation regex: /^[A-Za-z0-9_-]{16,128}$/.
// crypto.randomUUID() (36 chars, hex + hyphens) satisfies this, as does the
// getRandomValues hex fallback (32 chars).
const DEVICE_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

function generateRandomHex(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  // crypto.getRandomValues is available in every supported browser; if it is
  // somehow missing we fall back to Math.random (weaker, but the deviceId is
  // not a secret — it only gates the 7-day device-trust window).
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    cryptoObj.getRandomValues(bytes);
  } else {
    // Degraded path: surface it so operators notice if a browser lacks WebCrypto.
    // eslint-disable-next-line no-console
    console.warn(
      '[deviceId] crypto.getRandomValues unavailable — using a weaker fallback for the device id',
    );
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function generateDeviceId(): string {
  const cryptoObj = globalThis.crypto;
  // Preferred: native UUID (e.g. "1b2e...-..."), well within the 16-128 range.
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    try {
      return cryptoObj.randomUUID();
    } catch {
      // Some environments expose randomUUID but throw (e.g. non-secure
      // context); fall through to the getRandomValues hex fallback.
    }
  }
  // Fallback: 32-char random hex string (16 bytes), matches the validation
  // regex and gives 128 bits of entropy.
  return generateRandomHex(16);
}

/**
 * Returns the stable device id for this browser, creating and persisting one in
 * localStorage ('percix_device_id') on first use. The value is passed as
 * clientMetadata.deviceId on Auth.signIn / Auth.sendCustomChallengeAnswer so the
 * MFA Lambda can apply per-device trust.
 *
 * Defensive: if localStorage is unavailable (private mode quirks, blocked
 * storage) we still return a freshly generated id so sign-in is never blocked —
 * it just won't persist, which fails closed (MFA re-prompt) as intended.
 */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing && DEVICE_ID_PATTERN.test(existing)) {
      return existing;
    }
    const created = generateDeviceId();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, created);
    return created;
  } catch {
    // localStorage threw (access denied / quota). Return a non-persisted id.
    return generateDeviceId();
  }
}
